import api from './api'

import type {
  AgentsDailyReport,
  AgentsScorecard,
  AgentCallRow,
  Announcement,
  AnnouncementAudienceOptions,
  AnnouncementInput,
  AnnouncementRecipient,
  AppAccount,
  AppCampaign,
  AppCampaignInput,
  AppCampaignOptions,
  AppChatSettings,
  AppChatSummary,
  AppCustomerCreator,
  AppCustomersReport,
  AudiencePreview,
  BatchAcquisition,
  BookingConfirmations,
  CenterDistribution,
  CenterTarget,
  ChatAssignee,
  CommissionChange,
  ComplexDeleteCheck,
  ComplexFinance,
  ComplexFinanceInput,
  ComplexFinanceSummary,
  ComplexOption,
  CostCenterReport,
  CustomerFollowUpEntry,
  DeletedLead,
  DeletedLeadEdit,
  DeletedPhoneCheck,
  Developer,
  DeveloperInput,
  DueAlert,
  DuePaymentsReport,
  HomeSectionItem,
  HomeSectionKey,
  InboxChannel,
  InboxChannelInput,
  InboxSummary,
  JobApplicant,
  JobApplicantFile,
  JobApplicantFileKind,
  JobApplicantInput,
  JobApplicantNote,
  JobApplicantRow,
  JobApplicantStatus,
  MarketedProject,
  MarketedProjectInput,
  MarketingBanner,
  MarketingBannerInput,
  MarketingSettings,
  MediaLeadImportBatch,
  MediaLeadImportResult,
  MediaLeadImportRow,
  MihwarChannel,
  MostSearchedRow,
  PendingSale,
  PreliminaryReservation,
  ProjectFunnel,
  PublicJobTitle,
  PublicProjectDetail,
  QrSession,
  RealEstateBudget,
  RealEstateBudgetInput,
  RealEstateProfitReport,
  RecruitmentStats,
  SaleCommissionRow,
  SaleContract,
  SaleContractCreate,
  SaleContractRow,
  SaleDueInstallment,
  SalesOverdueRow,
  SalesSummary,
  SocialConversation,
  SocialConversationStatus,
  SocialMessage,
  StaffChatConversation,
  StaffChatMessage,
  StaffChatMessagesPage,
  TargetsReport,
  Testimonial,
  TestimonialInput,
} from '../types'
import type {
  Account,
  AccountTreeNode,
  AttendanceLog,
  AuthResponse,
  BalanceSheetReport,
  CostCenter,
  CrmLookup,
  Currency,
  Customer,
  CustomerBook,
  CustomerBookListRow,
  CustomerListRow,
  CustomerLookup,
  CustomerProfile,
  CustomerSimilar,
  DuplicateNameCluster,
  StaffEntryRow,
  Department,
  DepartmentTreeNode,
  Employee,
  EmployeeDocument,
  EmployeeListRow,
  EmployeeLookup,
  FileCompletenessReport,
  HrBulkResult,
  TaxPreview,
  TaxRule,
  TaxRuleInput,
  EmployeeLoan,
  EmployeeSalaryComponent,
  EmployeeViolation,
  FiscalYear,
  GeneralLedgerReport,
  IncomeStatementReport,
  Item,
  ItemGroup,
  ItemGroupTreeNode,
  ItemListRow,
  JobTitle,
  JournalEntry,
  LeaveBalance,
  LeaveRequest,
  LeaveType,
  LoanStatus,
  MyDashboard,
  PagedResult,
  PayrollRun,
  Payslip,
  PerformanceReview,
  PublicHoliday,
  SalaryComponent,
  Shift,
  EmployeeShiftAssignment,
  TimeExcuse,
  ManagerDeparture,
  HrAlertsSummary,
  HrLetter,
  FlexReportRow,
  InstantPunchRequest,
  InstantPunchTarget,
  BiometricDevice,
  DevicePunch,
  TimyStatus,
  Promotion,
  EmployeeTask,
  HrDashboard,
  PenalizedDay,
  PenaltyWaiver,
  OpenEndedReportRow,
  OrgChart,
  Separation,
  SeparationPreview,
  EmployeeAsset,
  RosterGrid,
  StockBalance,
  StockLedgerReport,
  StockMovement,
  TrainingCourse,
  TrainingEnrollment,
  TransactionReport,
  TrialBalanceReport,
  UnitOfMeasure,
  UserInfo,
  VisitAppointment,
  Warehouse,
  BranchActivity,
  VisitAttendanceRow,
  MediaLead,
  MediaLeadListResult,
  LeadHistory,
  MihwarContacts,
  MihwarDelivery,
  MihwarStatus,
  MihwarTest,
  MediaLeadStatus,
  NumberWorkspace,
  PhoneCheckResult,
  AgentsMonitor,
  AgentsQualityReport,
  BranchesReport,
  SalesActionsReport,
  UnifiedNumber,
  MediaPerformanceReport,
  CallCenterAgent,
  MediaEmployee,
  MediaNumbersReport,
} from '../types'

// Auth
export const authApi = {
  login: (email: string, password: string) =>
    api.post<AuthResponse>('/api/auth/login', { email, password }).then((r) => r.data),
  me: () => api.get<UserInfo>('/api/auth/me').then((r) => r.data),
  /** الموظف يغيّر كلمة مروره بنفسه — تعود جلسةٌ جديدة (الأجهزة الأخرى تخرج) */
  changePassword: (currentPassword: string, newPassword: string) =>
    api
      .post<AuthResponse>('/api/auth/change-password', { currentPassword, newPassword })
      .then((r) => r.data),
  register: (data: {
    email: string
    password: string
    fullNameAr: string
    fullNameEn: string
    role: string
  }) => api.post<UserInfo>('/api/auth/register', data).then((r) => r.data),
}

// ═══ Users Management (Admin only) ═══
import type {
  UserListItem,
  UserDetail,
  UserCreateInput,
  UserUpdateInput,
  MenuItemMeta,
} from '../types'

export const usersApi = {
  list: (params?: { search?: string; role?: string }) =>
    api.get<UserListItem[]>('/api/users', { params }).then((r) => r.data),
  get: (id: string) => api.get<UserDetail>(`/api/users/${id}`).then((r) => r.data),
  create: (dto: UserCreateInput) => api.post<UserDetail>('/api/users', dto).then((r) => r.data),
  update: (id: string, dto: UserUpdateInput) =>
    api.put<UserDetail>(`/api/users/${id}`, dto).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/users/${id}`),
  changePassword: (id: string, newPassword: string) =>
    api.post(`/api/users/${id}/change-password`, { newPassword }).then((r) => r.data),
  availableMenus: () => api.get<MenuItemMeta[]>('/api/users/available-menus').then((r) => r.data),
}

// ═══ خريطة الفريق (Admin) — روابط الموظفين بالمجمعات والمدراء ومشاريع الدردشة ═══
import type { TeamBulkResult, TeamLinkLogEntry, TeamLinksResult, TeamMapData } from '../types'

export const teamMapApi = {
  get: () => api.get<TeamMapData>('/api/team-map').then((r) => r.data),
  addLinks: (dto: {
    residentialComplexId: number
    employeeIds: number[]
    media: boolean
    teamLabel?: string
  }) => api.post<TeamLinksResult>('/api/team-map/links', dto).then((r) => r.data),
  removeLink: (complexId: number, employeeId: number, media: boolean, team?: string) =>
    api.delete(`/api/team-map/links/${complexId}/${employeeId}`, { params: { media, team } }),
  setManager: (employeeIds: number[], managerId: number | null) =>
    api
      .put<TeamBulkResult>('/api/team-map/managers', { employeeIds, managerId })
      .then((r) => r.data),
  setChatAssignee: (projectId: number, userId: string | null) =>
    api.put(`/api/team-map/projects/${projectId}/chat-assignee`, { userId }),
  cleanupStale: () =>
    api.post<{ removed: number }>('/api/team-map/stale-links/cleanup').then((r) => r.data),
  log: (take = 80) =>
    api.get<TeamLinkLogEntry[]>('/api/team-map/log', { params: { take } }).then((r) => r.data),
}

// Accounts
export const accountsApi = {
  tree: () => api.get<AccountTreeNode[]>('/api/accounts/tree').then((r) => r.data),
  leaf: () => api.get<Account[]>('/api/accounts/leaf').then((r) => r.data),
  nextCode: (parentId?: number) =>
    api
      .get<{ code: string }>('/api/accounts/next-code', { params: { parentId } })
      .then((r) => r.data.code),
  get: (id: number) => api.get<Account>(`/api/accounts/${id}`).then((r) => r.data),
  create: (dto: Partial<Account>) => api.post<Account>('/api/accounts', dto).then((r) => r.data),
  update: (id: number, dto: Partial<Account>) =>
    api.put<Account>(`/api/accounts/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/accounts/${id}`),
}

// Journal Entries
export const journalApi = {
  list: (params: Record<string, unknown>) =>
    api.get<PagedResult<JournalEntry>>('/api/journal-entries', { params }).then((r) => r.data),
  get: (id: number) => api.get<JournalEntry>(`/api/journal-entries/${id}`).then((r) => r.data),
  create: (dto: unknown) => api.post<JournalEntry>('/api/journal-entries', dto).then((r) => r.data),
  update: (id: number, dto: unknown) =>
    api.put<JournalEntry>(`/api/journal-entries/${id}`, dto).then((r) => r.data),
  post: (id: number) => api.post(`/api/journal-entries/${id}/post`),
  cancel: (id: number) => api.post(`/api/journal-entries/${id}/cancel`),
  /** حذف نهائي لقيدٍ تجريبي وسلسلة عكسه (أدمن) — يعيد أرقام المحذوف */
  purge: (id: number) =>
    api.delete<{ deleted: string[] }>(`/api/journal-entries/${id}/purge`).then((r) => r.data),
  opening: (fiscalYearId: number, dto: unknown) =>
    api.post<JournalEntry>(`/api/journal-entries/opening/${fiscalYearId}`, dto).then((r) => r.data),
}

// Lookups
export const lookupsApi = {
  currencies: () => api.get<Currency[]>('/api/currencies').then((r) => r.data),
  createCurrency: (dto: Partial<Currency>) =>
    api.post<Currency>('/api/currencies', dto).then((r) => r.data),
  updateCurrency: (id: number, dto: Partial<Currency>) =>
    api.put<Currency>(`/api/currencies/${id}`, dto).then((r) => r.data),

  costCenters: () => api.get<CostCenter[]>('/api/cost-centers').then((r) => r.data),
  createCostCenter: (dto: Partial<CostCenter>) =>
    api.post<CostCenter>('/api/cost-centers', dto).then((r) => r.data),
  updateCostCenter: (id: number, dto: Partial<CostCenter>) =>
    api.put<CostCenter>(`/api/cost-centers/${id}`, dto).then((r) => r.data),

  fiscalYears: () => api.get<FiscalYear[]>('/api/fiscal-years').then((r) => r.data),
  currentFiscalYear: () => api.get<FiscalYear>('/api/fiscal-years/current').then((r) => r.data),
  createFiscalYear: (dto: Partial<FiscalYear>) =>
    api.post<FiscalYear>('/api/fiscal-years', dto).then((r) => r.data),
}

// Reports
export const reportsApi = {
  generalLedger: (accountId: number, from: string, to: string, costCenterId?: number) =>
    api
      .get<GeneralLedgerReport>('/api/reports/general-ledger', {
        params: { accountId, from, to, costCenterId },
      })
      .then((r) => r.data),
  trialBalance: (asOfDate: string, fiscalYearId?: number, level?: number, costCenterId?: number) =>
    api
      .get<TrialBalanceReport>('/api/reports/trial-balance', {
        params: { asOfDate, fiscalYearId, level, costCenterId },
      })
      .then((r) => r.data),
  balanceSheet: (asOfDate: string) =>
    api
      .get<BalanceSheetReport>('/api/reports/balance-sheet', { params: { asOfDate } })
      .then((r) => r.data),
  incomeStatement: (from: string, to: string) =>
    api
      .get<IncomeStatementReport>('/api/reports/income-statement', { params: { from, to } })
      .then((r) => r.data),
  /** إيرادات ومصروفات وصافي كل مركز كلفة بحساباته (و«غير موزّع») */
  costCenters: (from: string, to: string, costCenterId?: number) =>
    api
      .get<CostCenterReport>('/api/reports/cost-centers', { params: { from, to, costCenterId } })
      .then((r) => r.data),
  sales: (from: string, to: string) =>
    api.get<TransactionReport>('/api/reports/sales', { params: { from, to } }).then((r) => r.data),
  purchases: (from: string, to: string) =>
    api
      .get<TransactionReport>('/api/reports/purchases', { params: { from, to } })
      .then((r) => r.data),
  receiptVouchers: (from: string, to: string, costCenterId?: number) =>
    api
      .get<TransactionReport>('/api/reports/receipt-vouchers', {
        params: { from, to, costCenterId },
      })
      .then((r) => r.data),
  paymentVouchers: (from: string, to: string, costCenterId?: number) =>
    api
      .get<TransactionReport>('/api/reports/payment-vouchers', {
        params: { from, to, costCenterId },
      })
      .then((r) => r.data),
}

// ============ Inventory ============

export const unitsApi = {
  all: () => api.get<UnitOfMeasure[]>('/api/units').then((r) => r.data),
  create: (dto: Partial<UnitOfMeasure>) =>
    api.post<UnitOfMeasure>('/api/units', dto).then((r) => r.data),
  update: (id: number, dto: Partial<UnitOfMeasure>) =>
    api.put<UnitOfMeasure>(`/api/units/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/units/${id}`),
}

export const warehousesApi = {
  all: () => api.get<Warehouse[]>('/api/warehouses').then((r) => r.data),
  create: (dto: Partial<Warehouse>) =>
    api.post<Warehouse>('/api/warehouses', dto).then((r) => r.data),
  update: (id: number, dto: Partial<Warehouse>) =>
    api.put<Warehouse>(`/api/warehouses/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/warehouses/${id}`),
}

export const itemGroupsApi = {
  tree: () => api.get<ItemGroupTreeNode[]>('/api/item-groups/tree').then((r) => r.data),
  all: () => api.get<ItemGroup[]>('/api/item-groups').then((r) => r.data),
  get: (id: number) => api.get<ItemGroup>(`/api/item-groups/${id}`).then((r) => r.data),
  create: (dto: Partial<ItemGroup>) =>
    api.post<ItemGroup>('/api/item-groups', dto).then((r) => r.data),
  update: (id: number, dto: Partial<ItemGroup>) =>
    api.put<ItemGroup>(`/api/item-groups/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/item-groups/${id}`),
}

export const itemsApi = {
  search: (params: Record<string, unknown>) =>
    api.get<PagedResult<ItemListRow>>('/api/items', { params }).then((r) => r.data),
  get: (id: number) => api.get<Item>(`/api/items/${id}`).then((r) => r.data),
  getByBarcode: (code: string) =>
    api.get<Item>(`/api/items/barcode/${encodeURIComponent(code)}`).then((r) => r.data),
  create: (dto: unknown) => api.post<Item>('/api/items', dto).then((r) => r.data),
  update: (id: number, dto: unknown) => api.put<Item>(`/api/items/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/items/${id}`),
  uploadImage: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ imagePath: string }>(`/api/items/${id}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
}

// ============ HR ============

export const departmentsApi = {
  tree: () => api.get<DepartmentTreeNode[]>('/api/departments/tree').then((r) => r.data),
  all: () => api.get<Department[]>('/api/departments').then((r) => r.data),
  get: (id: number) => api.get<Department>(`/api/departments/${id}`).then((r) => r.data),
  create: (dto: Partial<Department>) =>
    api.post<Department>('/api/departments', dto).then((r) => r.data),
  update: (id: number, dto: Partial<Department>) =>
    api.put<Department>(`/api/departments/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/departments/${id}`),
}

export const jobTitlesApi = {
  all: () => api.get<JobTitle[]>('/api/job-titles').then((r) => r.data),
  create: (dto: Partial<JobTitle>) =>
    api.post<JobTitle>('/api/job-titles', dto).then((r) => r.data),
  update: (id: number, dto: Partial<JobTitle>) =>
    api.put<JobTitle>(`/api/job-titles/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/job-titles/${id}`),
}

// ============ HR Phases 2-6 ============

export const shiftsApi = {
  all: () => api.get<Shift[]>('/api/shifts').then((r) => r.data),
  create: (dto: Partial<Shift>) => api.post<Shift>('/api/shifts', dto).then((r) => r.data),
  update: (id: number, dto: Partial<Shift>, force = false) =>
    api.put<Shift>(`/api/shifts/${id}`, dto, { params: { force } }).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/shifts/${id}`),
  /** معاينة شرائح التأخير — تُحتسب بمحرك الخادم لا بنسخة في الواجهة */
  latePenaltyPreview: (dto: {
    tiersCsv?: string
    latePenaltyPerMinute?: number
    sampleMinutes?: number[]
  }) =>
    api
      .post<{
        isValid: boolean
        error?: string | null
        isFixedAmount: boolean
        description: string
        minuteRate: number
        samples: { minutes: number; amount: number; tierLabel?: string | null }[]
      }>('/api/shifts/late-penalty-preview', dto)
      .then((r) => r.data),
  // إسناد الشفتات للموظفين (بفترة سريان)
  assignments: (params?: { employeeId?: number; shiftId?: number }) =>
    api.get<EmployeeShiftAssignment[]>('/api/shifts/assignments', { params }).then((r) => r.data),
  assign: (dto: {
    employeeId: number
    shiftId: number
    effectiveFrom: string
    effectiveTo?: string | null
    notes?: string | null
  }) => api.post<EmployeeShiftAssignment>('/api/shifts/assignments', dto).then((r) => r.data),
  /** إسناد شفت لقسم كامل — يُرجع من نجح ومن تُخطّي ولماذا */
  assignBulk: (dto: {
    shiftId: number
    departmentId?: number
    employeeIds?: number[]
    effectiveFrom: string
    effectiveTo?: string | null
    notes?: string | null
  }) => api.post<HrBulkResult>('/api/shifts/assignments/bulk', dto).then((r) => r.data),
  unassign: (id: number) => api.delete(`/api/shifts/assignments/${id}`),
}

export const flexApi = {
  report: (from: string, to: string, departmentId?: number) =>
    api
      .get<FlexReportRow[]>('/api/flex/report', { params: { from, to, departmentId } })
      .then((r) => r.data),
  deleteSession: (id: number) => api.delete(`/api/flex/sessions/${id}`),
}

export const instantPunchApi = {
  all: (take = 50) =>
    api.get<InstantPunchRequest[]>('/api/instant-punch', { params: { take } }).then((r) => r.data),
  create: (dto: {
    scopeType: string
    departmentId?: number | null
    employeeIds?: number[]
    note?: string | null
    windowMinutes?: number
  }) => api.post<InstantPunchRequest>('/api/instant-punch', dto).then((r) => r.data),
  targets: (id: number) =>
    api.get<InstantPunchTarget[]>(`/api/instant-punch/${id}/targets`).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/instant-punch/${id}`),
}

export const devicesApi = {
  all: (probeOnline = false) =>
    api.get<BiometricDevice[]>('/api/devices', { params: { probeOnline } }).then((r) => r.data),
  create: (dto: Record<string, unknown>) =>
    api.post<BiometricDevice>('/api/devices', dto).then((r) => r.data),
  update: (id: number, dto: Record<string, unknown>) =>
    api.put<BiometricDevice>(`/api/devices/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/devices/${id}`),
  status: () => api.get<TimyStatus>('/api/devices/status').then((r) => r.data),
  pull: (id: number, days = 1) =>
    api
      .post<{
        ok: boolean
        message: string
        fetched: number
        applied: number
      }>(`/api/devices/${id}/pull`, null, { params: { days } })
      .then((r) => r.data),
  punches: (params: {
    deviceId?: number
    status?: string
    from?: string
    to?: string
    take?: number
  }) => api.get<DevicePunch[]>('/api/devices/punches', { params }).then((r) => r.data),
  linkEnroll: (dto: { enrollId: number; employeeId: number; reprocessPast?: boolean }) =>
    api
      .post<{ applied: number; message: string }>('/api/devices/link-enroll', dto)
      .then((r) => r.data),
  pushEmployee: (deviceId: number, employeeId: number) =>
    api
      .post<{ message: string }>(`/api/devices/${deviceId}/push-employee/${employeeId}`)
      .then((r) => r.data),
  removeEmployee: (employeeId: number) =>
    api.delete<{ message: string }>(`/api/devices/employees/${employeeId}`).then((r) => r.data),
}

export const promotionsApi = {
  all: (employeeId?: number, status?: number) =>
    api.get<Promotion[]>('/api/promotions', { params: { employeeId, status } }).then((r) => r.data),
  create: (dto: Record<string, unknown>) =>
    api.post<Promotion>('/api/promotions', dto).then((r) => r.data),
  approve: (id: number) => api.post<Promotion>(`/api/promotions/${id}/approve`).then((r) => r.data),
  cancel: (id: number) => api.post(`/api/promotions/${id}/cancel`),
}

export const tasksApi = {
  search: (params: {
    employeeId?: number
    departmentId?: number
    state?: string
    from?: string
    to?: string
  }) => api.get<EmployeeTask[]>('/api/tasks', { params }).then((r) => r.data),
  assign: (dto: Record<string, unknown>) =>
    api.post<{ assigned: number; message: string }>('/api/tasks', dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/tasks/${id}`),
  removeBatch: (batchId: string) => api.delete(`/api/tasks/batch/${batchId}`),
  resolveBlocker: (
    id: number,
    dto: { acceptRequestedDate?: boolean; newDueDate?: string | null },
  ) => api.post<EmployeeTask>(`/api/tasks/${id}/resolve-blocker`, dto).then((r) => r.data),
}

export const hrReportsApi = {
  fileCompleteness: (departmentId?: number) =>
    api
      .get<FileCompletenessReport>('/api/hr-reports/file-completeness', {
        params: { departmentId },
      })
      .then((r) => r.data),
  dashboard: (from: string, to: string, departmentId?: number) =>
    api
      .get<HrDashboard>('/api/hr-reports/dashboard', { params: { from, to, departmentId } })
      .then((r) => r.data),
}

export const penaltyWaiversApi = {
  penalizedDays: (params: {
    from: string
    to: string
    employeeId?: number
    departmentId?: number
    includeWaived?: boolean
  }) =>
    api.get<PenalizedDay[]>('/api/penalty-waivers/penalized-days', { params }).then((r) => r.data),
  all: (params: { from?: string; to?: string; employeeId?: number; includeReverted?: boolean }) =>
    api.get<PenaltyWaiver[]>('/api/penalty-waivers', { params }).then((r) => r.data),
  waive: (dto: { employeeId: number; date: string; reason: string }) =>
    api.post<PenaltyWaiver>('/api/penalty-waivers', dto).then((r) => r.data),
  revert: (id: number, reason?: string) =>
    api.post<PenaltyWaiver>(`/api/penalty-waivers/${id}/revert`, { reason }).then((r) => r.data),
}

export const openEndedReportApi = {
  get: (from: string, to: string, departmentId?: number) =>
    api
      .get<OpenEndedReportRow[]>('/api/open-ended-report', { params: { from, to, departmentId } })
      .then((r) => r.data),
}

export const hrLettersApi = {
  all: (employeeId?: number) =>
    api.get<HrLetter[]>('/api/hr-letters', { params: { employeeId } }).then((r) => r.data),
  issue: (dto: Record<string, unknown>) =>
    api.post<HrLetter>('/api/hr-letters', dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/hr-letters/${id}`),
}

export const orgChartApi = {
  get: (departmentId?: number) =>
    api.get<OrgChart>('/api/org-chart', { params: { departmentId } }).then((r) => r.data),
}

export const hrAlertsApi = {
  get: (withinDays = 60, departmentId?: number) =>
    api
      .get<HrAlertsSummary>('/api/hr-alerts', { params: { withinDays, departmentId } })
      .then((r) => r.data),
}

export const separationsApi = {
  all: (status?: string) =>
    api.get<Separation[]>('/api/separations', { params: { status } }).then((r) => r.data),
  get: (id: number) => api.get<Separation>(`/api/separations/${id}`).then((r) => r.data),
  preview: (employeeId: number, lastWorkingDay: string, gratuityDaysPerYear: number) =>
    api
      .get<SeparationPreview>('/api/separations/preview', {
        params: { employeeId, lastWorkingDay, gratuityDaysPerYear },
      })
      .then((r) => r.data),
  create: (dto: Record<string, unknown>) =>
    api.post<Separation>('/api/separations', dto).then((r) => r.data),
  approve: (id: number) =>
    api.post<Separation>(`/api/separations/${id}/approve`).then((r) => r.data),
  settle: (id: number) => api.post<Separation>(`/api/separations/${id}/settle`).then((r) => r.data),
  cancel: (id: number) => api.post(`/api/separations/${id}/cancel`),
}

export const employeeAssetsApi = {
  all: (params?: { employeeId?: number; outstandingOnly?: boolean }) =>
    api.get<EmployeeAsset[]>('/api/employee-assets', { params }).then((r) => r.data),
  issue: (dto: Record<string, unknown>) =>
    api.post<EmployeeAsset>('/api/employee-assets', dto).then((r) => r.data),
  returnAsset: (id: number, dto: { returnedDate: string; returnCondition?: string | null }) =>
    api.post<EmployeeAsset>(`/api/employee-assets/${id}/return`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/employee-assets/${id}`),
}

export const dayExceptionsApi = {
  excuses: (params?: { from?: string; to?: string; employeeId?: number }) =>
    api.get<TimeExcuse[]>('/api/time-excuses', { params }).then((r) => r.data),
  grantExcuse: (dto: {
    employeeIds: number[]
    date: string
    lateGraceMinutes: number
    earlyLeaveMinutes: number
    flexExemptMinutes?: number
    note?: string | null
  }) => api.post<{ granted: number }>('/api/time-excuses', dto).then((r) => r.data),
  deleteExcuse: (id: number) => api.delete(`/api/time-excuses/${id}`),

  departures: (params?: { from?: string; to?: string; employeeId?: number }) =>
    api.get<ManagerDeparture[]>('/api/manager-departures', { params }).then((r) => r.data),
  grantDeparture: (dto: {
    employeeIds: number[]
    date: string
    departureTime: string
    note?: string | null
  }) => api.post<{ granted: number }>('/api/manager-departures', dto).then((r) => r.data),
  deleteDeparture: (id: number) => api.delete(`/api/manager-departures/${id}`),
}

export const rosterApi = {
  grid: (params: { from: string; to: string; departmentId?: number; employeeId?: number }) =>
    api.get<RosterGrid>('/api/roster', { params }).then((r) => r.data),
  bulkAssign: (dto: {
    employeeIds: number[]
    dates: string[]
    shiftId: number
    notes?: string | null
    overwrite?: boolean
  }) => api.post<{ assigned: number }>('/api/roster/entries', dto).then((r) => r.data),
  removeEntry: (id: number) => api.delete(`/api/roster/entries/${id}`),
  clear: (dto: { employeeIds: number[]; from: string; to: string }) =>
    api.post<{ removed: number }>('/api/roster/clear', dto).then((r) => r.data),
}

export const publicHolidaysApi = {
  all: (year?: number) =>
    api.get<PublicHoliday[]>('/api/public-holidays', { params: { year } }).then((r) => r.data),
  create: (dto: Partial<PublicHoliday>) =>
    api.post<PublicHoliday>('/api/public-holidays', dto).then((r) => r.data),
  update: (id: number, dto: Partial<PublicHoliday>) =>
    api.put<PublicHoliday>(`/api/public-holidays/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/public-holidays/${id}`),
}

export const attendanceApi = {
  search: (params: Record<string, unknown>) =>
    api.get<PagedResult<AttendanceLog>>('/api/attendance', { params }).then((r) => r.data),
  manualEntry: (dto: unknown) =>
    api.post<AttendanceLog>('/api/attendance', dto).then((r) => r.data),
  checkIn: (employeeId: number, dto: { latitude?: number; longitude?: number; notes?: string }) =>
    api.post<AttendanceLog>(`/api/attendance/${employeeId}/check-in`, dto).then((r) => r.data),
  checkOut: (employeeId: number, dto: { latitude?: number; longitude?: number; notes?: string }) =>
    api.post<AttendanceLog>(`/api/attendance/${employeeId}/check-out`, dto).then((r) => r.data),
  importExcel: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ imported: number }>('/api/attendance/import-excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
}

export const leaveTypesApi = {
  all: () => api.get<LeaveType[]>('/api/leave-types').then((r) => r.data),
  create: (dto: Partial<LeaveType>) =>
    api.post<LeaveType>('/api/leave-types', dto).then((r) => r.data),
  update: (id: number, dto: Partial<LeaveType>) =>
    api.put<LeaveType>(`/api/leave-types/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/leave-types/${id}`),
}

export const leavesApi = {
  balances: (employeeId?: number, year?: number) =>
    api
      .get<LeaveBalance[]>('/api/leaves/balances', { params: { employeeId, year } })
      .then((r) => r.data),
  recalculate: (employeeId: number, year: number) =>
    api.post('/api/leaves/balances/recalculate', null, { params: { employeeId, year } }),
  /** توليد أرصدة السنة لكل الموظفين النشطين دفعة واحدة */
  generateAll: (year: number) =>
    api
      .post<{ count: number }>('/api/leaves/balances/generate', null, { params: { year } })
      .then((r) => r.data),
  requests: (params: Record<string, unknown>) =>
    api.get<LeaveRequest[]>('/api/leaves/requests', { params }).then((r) => r.data),
  getRequest: (id: number) =>
    api.get<LeaveRequest>(`/api/leaves/requests/${id}`).then((r) => r.data),
  submit: (dto: unknown) => api.post<LeaveRequest>('/api/leaves/requests', dto).then((r) => r.data),
  approve: (id: number) =>
    api.post<LeaveRequest>(`/api/leaves/requests/${id}/approve`).then((r) => r.data),
  reject: (id: number, reason: string) =>
    api.post<LeaveRequest>(`/api/leaves/requests/${id}/reject`, { reason }).then((r) => r.data),
  cancel: (id: number) =>
    api.post<LeaveRequest>(`/api/leaves/requests/${id}/cancel`).then((r) => r.data),
}

export const salaryComponentsApi = {
  all: () => api.get<SalaryComponent[]>('/api/salary-components').then((r) => r.data),
  create: (dto: Partial<SalaryComponent>) =>
    api.post<SalaryComponent>('/api/salary-components', dto).then((r) => r.data),
  update: (id: number, dto: Partial<SalaryComponent>) =>
    api.put<SalaryComponent>(`/api/salary-components/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/salary-components/${id}`),
  forEmployee: (employeeId: number) =>
    api
      .get<EmployeeSalaryComponent[]>(`/api/salary-components/employee/${employeeId}`)
      .then((r) => r.data),
  setForEmployee: (dto: unknown) =>
    api.post<EmployeeSalaryComponent>('/api/salary-components/employee', dto).then((r) => r.data),
  deleteForEmployee: (id: number) => api.delete(`/api/salary-components/employee/${id}`),
}

export const payrollApi = {
  runs: () => api.get<PayrollRun[]>('/api/payroll/runs').then((r) => r.data),
  getRun: (id: number) => api.get<PayrollRun>(`/api/payroll/runs/${id}`).then((r) => r.data),
  runPayslips: (id: number) =>
    api.get<Payslip[]>(`/api/payroll/runs/${id}/payslips`).then((r) => r.data),
  payslip: (id: number) => api.get<Payslip>(`/api/payroll/payslips/${id}`).then((r) => r.data),
  employeePayslips: (employeeId: number, year?: number) =>
    api
      .get<Payslip[]>(`/api/payroll/employee/${employeeId}/payslips`, { params: { year } })
      .then((r) => r.data),
  calculate: (dto: unknown) =>
    api.post<PayrollRun>('/api/payroll/runs/calculate', dto).then((r) => r.data),
  approve: (id: number) =>
    api.post<PayrollRun>(`/api/payroll/runs/${id}/approve`).then((r) => r.data),
  markPaid: (id: number) =>
    api.post<PayrollRun>(`/api/payroll/runs/${id}/mark-paid`).then((r) => r.data),
  deleteRun: (id: number) => api.delete(`/api/payroll/runs/${id}`),
}

export const loansApi = {
  all: (employeeId?: number, status?: LoanStatus) =>
    api.get<EmployeeLoan[]>('/api/loans', { params: { employeeId, status } }).then((r) => r.data),
  get: (id: number) => api.get<EmployeeLoan>(`/api/loans/${id}`).then((r) => r.data),
  create: (dto: unknown) => api.post<EmployeeLoan>('/api/loans', dto).then((r) => r.data),
  approve: (id: number) => api.post<EmployeeLoan>(`/api/loans/${id}/approve`).then((r) => r.data),
  cancel: (id: number) => api.post<EmployeeLoan>(`/api/loans/${id}/cancel`).then((r) => r.data),
}

export const violationsApi = {
  all: (params: Record<string, unknown>) =>
    api.get<EmployeeViolation[]>('/api/violations', { params }).then((r) => r.data),
  create: (dto: unknown) => api.post<EmployeeViolation>('/api/violations', dto).then((r) => r.data),
  update: (id: number, dto: unknown) =>
    api.put<EmployeeViolation>(`/api/violations/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/violations/${id}`),
}

export const performanceApi = {
  all: (employeeId?: number, period?: string) =>
    api
      .get<PerformanceReview[]>('/api/performance', { params: { employeeId, period } })
      .then((r) => r.data),
  get: (id: number) => api.get<PerformanceReview>(`/api/performance/${id}`).then((r) => r.data),
  create: (dto: unknown) =>
    api.post<PerformanceReview>('/api/performance', dto).then((r) => r.data),
  update: (id: number, dto: unknown) =>
    api.put<PerformanceReview>(`/api/performance/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/performance/${id}`),
}

export const trainingApi = {
  courses: () => api.get<TrainingCourse[]>('/api/training/courses').then((r) => r.data),
  createCourse: (dto: Partial<TrainingCourse>) =>
    api.post<TrainingCourse>('/api/training/courses', dto).then((r) => r.data),
  updateCourse: (id: number, dto: Partial<TrainingCourse>) =>
    api.put<TrainingCourse>(`/api/training/courses/${id}`, dto).then((r) => r.data),
  deleteCourse: (id: number) => api.delete(`/api/training/courses/${id}`),
  enrollments: (params: Record<string, unknown>) =>
    api.get<TrainingEnrollment[]>('/api/training/enrollments', { params }).then((r) => r.data),
  enroll: (dto: unknown) =>
    api.post<TrainingEnrollment>('/api/training/enrollments', dto).then((r) => r.data),
  updateEnrollment: (id: number, dto: unknown) =>
    api.put<TrainingEnrollment>(`/api/training/enrollments/${id}`, dto).then((r) => r.data),
  deleteEnrollment: (id: number) => api.delete(`/api/training/enrollments/${id}`),
}

// ============ CRM ============

function makeLookupApi(base: string) {
  return {
    all: () => api.get<CrmLookup[]>(base).then((r) => r.data),
    create: (dto: Partial<CrmLookup>) => api.post<CrmLookup>(base, dto).then((r) => r.data),
    update: (id: number, dto: Partial<CrmLookup>) =>
      api.put<CrmLookup>(`${base}/${id}`, dto).then((r) => r.data),
    remove: (id: number) => api.delete(`${base}/${id}`),
  }
}

export const crmLookups = {
  provinces: makeLookupApi('/api/provinces'),
  professions: makeLookupApi('/api/professions'),
  sectors: makeLookupApi('/api/sectors'),
  ministries: makeLookupApi('/api/ministries'),
  residentialComplexes: {
    ...makeLookupApi('/api/residential-complexes'),
    // الأسماء وحدها (متاحة للجميع) — القائمة الكاملة بأعداد الوحدات لقرّاء المجمعات فقط
    all: () =>
      api
        .get<CrmLookup[]>('/api/residential-complexes', { params: { simple: true } })
        .then((r) => r.data),
  },
  callSources: makeLookupApi('/api/call-sources'),
  callStatuses: makeLookupApi('/api/call-statuses'),
  callTypes: makeLookupApi('/api/call-types'),
  customerQualities: makeLookupApi('/api/customer-qualities'),
  customerStatuses: makeLookupApi('/api/customer-statuses'),
  receptionEmployees: makeLookupApi('/api/reception-employees'),
  managers: makeLookupApi('/api/crm-managers'),
}

export const customersApi = {
  search: (params: Record<string, unknown>) =>
    api.get<PagedResult<CustomerListRow>>('/api/customers', { params }).then((r) => r.data),
  get: (id: number) => api.get<Customer>(`/api/customers/${id}`).then((r) => r.data),
  // معلومات الزبون — الملف الكامل (الخط الزمني لكل الحركات)
  profile: (id: number) =>
    api.get<CustomerProfile>(`/api/customers/${id}/profile`).then((r) => r.data),
  getByPhone: (phone: string) =>
    api.get<Customer>(`/api/customers/by-phone/${encodeURIComponent(phone)}`).then((r) => r.data),
  // كشف ذكي للأسماء (اسم جزئي/ثلاثي/أول اسم أو هاتف)
  lookup: (q: string, take = 10) =>
    api.get<CustomerLookup[]>('/api/customers/lookup', { params: { q, take } }).then((r) => r.data),
  // تقرير الأسماء المتشابهة على أكثر من مجمع
  duplicateNames: () =>
    api.get<DuplicateNameCluster[]>('/api/crm/duplicate-names').then((r) => r.data),
  // تقرير إدخال الموظفين (تطبيق مقابل خدمة العملاء) خلال مدة
  staffEntryReport: (params: { from?: string; to?: string }) =>
    api.get<StaffEntryRow[]>('/api/crm/staff-entry-report', { params }).then((r) => r.data),
  // فحص تكرار الاسم وقت الإدخال (أول ثلاثة أسماء)
  similarName: (name: string, excludePhone?: string, take = 5) =>
    api
      .get<CustomerSimilar[]>('/api/customers/similar-name', {
        params: { name, excludePhone, take },
      })
      .then((r) => r.data),
  create: (dto: unknown) => api.post<Customer>('/api/customers', dto).then((r) => r.data),
  update: (id: number, dto: unknown) =>
    api.put<Customer>(`/api/customers/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/customers/${id}`),
}

/** «تأكيد الحجوزات»: حجوزات «إضافة معلومات الزبائن» بانتظار اتصال التأكيد قبل مدير الفرع */
export const bookingConfirmationsApi = {
  list: (params: {
    status?: string
    from?: string
    to?: string
    complexId?: number
    /** فلتر المدير: من أضاف السطر */
    addedBy?: string
  }) => api.get<BookingConfirmations>('/api/booking-confirmations', { params }).then((r) => r.data),
  count: () => api.get<{ pending: number }>('/api/booking-confirmations/count').then((r) => r.data),
  confirm: (id: number, dto: { visitDate?: string | null; note?: string | null }) =>
    api.post(`/api/booking-confirmations/${id}/confirm`, dto).then((r) => r.data),
  noAnswer: (id: number, note?: string | null) =>
    api.post(`/api/booking-confirmations/${id}/no-answer`, { note }).then((r) => r.data),
  cancel: (id: number, note: string) =>
    api.post(`/api/booking-confirmations/${id}/cancel`, { note }).then((r) => r.data),
  reopen: (id: number) => api.post(`/api/booking-confirmations/${id}/reopen`).then((r) => r.data),
  /** «لم يؤكدوا الحضور»: اتصل واتفقا على موعد — يُؤكَّد فوراً ويصل لمدير الفرع */
  confirmAttendance: (id: number, dto: { visitDate: string; note?: string | null }) =>
    api.post(`/api/booking-confirmations/${id}/confirm-attendance`, dto).then((r) => r.data),
  /** «لم يؤكدوا الحضور» ولم يرد — ينتقل لتبويب «لم يرد» */
  unconfirmedNoAnswer: (id: number, note?: string | null) =>
    api
      .post(`/api/booking-confirmations/${id}/unconfirmed-no-answer`, { note })
      .then((r) => r.data),
  /** «لم يرد»: إعادة استهداف المحدد على مجمعٍ آخر */
  retargetNoAnswer: (dto: {
    followUpIds: number[]
    targetComplexId: number
    assignToEmployeeId?: number
    notes?: string
  }) =>
    api
      .post<{
        created: number
        skipped: number
        targetComplexName?: string
      }>('/api/booking-confirmations/no-answer/retarget', dto)
      .then((r) => r.data),
}

export const customerBooksApi = {
  all: () => api.get<CustomerBookListRow[]>('/api/customer-books').then((r) => r.data),
  get: (id: number) => api.get<CustomerBook>(`/api/customer-books/${id}`).then((r) => r.data),
  nextSequence: () =>
    api.get<{ sequence: number }>('/api/customer-books/next-sequence').then((r) => r.data.sequence),
  save: (dto: unknown) => api.post<CustomerBook>('/api/customer-books', dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/customer-books/${id}`),
  visitAppointments: (params: { from?: string; to?: string; upcomingOnly?: boolean }) =>
    api
      .get<VisitAppointment[]>('/api/customer-books/visit-appointments', { params })
      .then((r) => r.data),
  myVisitAppointments: (params: { from?: string; to?: string; upcomingOnly?: boolean }) =>
    api
      .get<VisitAppointment[]>('/api/customer-books/my-visit-appointments', { params })
      .then((r) => r.data),
  // تقرير متابعة الأفرع (حركات الوحدات + ملاحظات الاهتمام)
  branchActivity: (params: { from?: string; to?: string; complexId?: number; type?: string }) =>
    api.get<BranchActivity[]>('/api/crm/branch-activity', { params }).then((r) => r.data),
  // تقرير حضور الزيارات (حضر / لم يحضر)
  visitAttendance: (params: { from?: string; to?: string; complexId?: number; filter?: string }) =>
    api
      .get<VisitAttendanceRow[]>('/api/customer-books/visit-attendance', { params })
      .then((r) => r.data),
  setVisitAttendance: (followUpId: number, attendance: number) =>
    api
      .put(`/api/customer-books/follow-ups/${followUpId}/attendance`, { attendance })
      .then((r) => r.data),
}

// ═══ Media Leads (عملاء الميديا → توزيع خدمة العملاء) ═══
export const mediaLeadsApi = {
  // ── مركز عمليات الرقم: كل إدخالات رقمٍ مكرر — تعديل ودمج وحذف مُسجَّل ──
  number: (phone: string, dialCode?: string | null) =>
    api
      .get<NumberWorkspace>('/api/media-leads/number', { params: { phone, dialCode } })
      .then((r) => r.data),
  updateEntry: (id: number, dto: { customerName: string; notes?: string | null }) =>
    api.put(`/api/media-leads/${id}/entry`, dto).then((r) => r.data),
  renameNumber: (dto: { phone: string; countryDialCode?: string | null; name: string }) =>
    api.post<{ changed: number }>('/api/media-leads/number/rename', dto).then((r) => r.data),
  mergeNumber: (dto: { keepId: number; mergeIds: number[]; name?: string | null }) =>
    api.post<{ merged: number }>('/api/media-leads/number/merge', dto).then((r) => r.data),
  removeEntry: (id: number, reason: string) =>
    api.post(`/api/media-leads/${id}/remove`, { reason }),
  /** حذف مجموعة بسببٍ واحد — ما لا يُحذف (مرتبط بحجز/أصلٌ للأدمن) يعود بسببه */
  removeMany: (leadIds: number[], reason: string) =>
    api
      .post<{ removed: number; skipped: string[] }>('/api/media-leads/remove-many', {
        leadIds,
        reason,
      })
      .then((r) => r.data),
  // ── الأرقام المحذوفة: استرجاع، تعديل، تصحيح الرقم، حذف نهائي ──
  deleted: (params: { from?: string; to?: string; q?: string }) =>
    api.get<DeletedLead[]>('/api/media-leads/deleted', { params }).then((r) => r.data),
  /** فحص الرقم المصحَّح قبل الاسترجاع — عدا الإدخال المعدَّل نفسه */
  checkDeletedPhone: (params: {
    phone: string
    dialCode?: string
    excludeId?: number
    excludeLogId?: number
  }) =>
    api
      .get<DeletedPhoneCheck>('/api/media-leads/deleted/check-phone', { params })
      .then((r) => r.data),
  updateDeleted: (id: number, dto: DeletedLeadEdit) =>
    api.put(`/api/media-leads/deleted/${id}`, dto).then((r) => r.data),
  restoreDeleted: (id: number, dto?: DeletedLeadEdit) =>
    api
      .post<{ id: number }>(`/api/media-leads/deleted/${id}/restore`, dto ?? {})
      .then((r) => r.data),
  restoreLegacy: (logId: number, dto?: DeletedLeadEdit) =>
    api
      .post<{ id: number }>(`/api/media-leads/deleted/log/${logId}/restore`, dto ?? {})
      .then((r) => r.data),
  purgeDeleted: (id: number) => api.delete(`/api/media-leads/deleted/${id}`),
  /** إدخال قديم (حُذف قبل السلة) يُحذف نهائياً بسطر سجله */
  purgeLegacy: (logId: number) => api.delete(`/api/media-leads/deleted/log/${logId}`),
  // فحص الرقم أثناء الإدخال: هل سبق تسجيله، وباسم مَن، ومَن أضافه
  checkPhone: (phone: string, dialCode = '+964') =>
    api
      .get<PhoneCheckResult>('/api/media-leads/check-phone', { params: { phone, dialCode } })
      .then((r) => r.data),
  // مدير خدمة العملاء
  all: (params: {
    status?: MediaLeadStatus
    complexId?: number
    mediaEmployeeId?: number
    from?: string
    to?: string
    origin?: 'Media' | 'Retarget' | 'SalesRetarget' | 'Excel'
    /** العدّادات وحدها بلا صفوف — لوحة «متابعة خدمة العملاء» */
    statsOnly?: boolean
  }) => api.get<MediaLeadListResult>('/api/media-leads', { params }).then((r) => r.data),
  /** استيراد دفعة أسماء من إكسل — dryRun للمعاينة: حال كل صف (جديد / موجود مسبقاً / مكرر بالملف / غير صالح) بلا حفظ */
  importLeads: (dto: {
    fileName: string
    residentialComplexId?: number
    notes?: string
    dryRun?: boolean
    skipExisting?: boolean
    assignToEmployeeId?: number
    rows: MediaLeadImportRow[]
  }) => api.post<MediaLeadImportResult>('/api/media-leads/import', dto).then((r) => r.data),
  /** دفعات الاستيراد من إكسل بعدّاداتها وما آلت إليه أسماؤها */
  imports: (params: { from?: string; to?: string }) =>
    api.get<MediaLeadImportBatch[]>('/api/media-leads/imports', { params }).then((r) => r.data),
  // تقرير أداء موظفي الميديا
  employeesPerformance: (params: { from?: string; to?: string; complexId?: number }) =>
    api
      .get<MediaPerformanceReport>('/api/media-leads/employees-performance', { params })
      .then((r) => r.data),
  // السجل الموحّد: كل الأرقام من كل الأقسام
  unifiedNumbers: (params: { from?: string; to?: string; complexId?: number; dept?: string }) =>
    api.get<UnifiedNumber[]>('/api/media-leads/unified-numbers', { params }).then((r) => r.data),
  // إعادة استهداف أرقام من السجل الموحّد (منشأ Retarget — تبويب مستقل)
  retargetNumbers: (dto: {
    items: {
      customerName: string
      phone: string
      countryDialCode?: string | null
      country?: string | null
      previousNote?: string | null
    }[]
    targetComplexId: number
    assignToEmployeeId?: number
    notes?: string
  }) =>
    api
      .post<{ created: number; skipped: number }>('/api/media-leads/retarget-numbers', dto)
      .then((r) => r.data),
  assign: (leadIds: number[], employeeId: number) =>
    api.post('/api/media-leads/assign', { leadIds, employeeId }).then((r) => r.data),
  /** سحب التوزيع: المهام المفتوحة تعود «بانتظار التوزيع» ويُبلَّغ من كانت لديه */
  unassign: (leadIds: number[], reason?: string) =>
    api
      .post<{ count: number; message: string }>('/api/media-leads/unassign', { leadIds, reason })
      .then((r) => r.data),
  // إعادة استهداف أسماء (غير مهتم/لا يرد) على مجمع آخر
  retarget: (dto: {
    leadIds: number[]
    targetComplexId: number
    assignToEmployeeId?: number
    notes?: string
  }) =>
    api
      .post<{ created: number; skipped: number }>('/api/media-leads/retarget', dto)
      .then((r) => r.data),
  agents: () => api.get<CallCenterAgent[]>('/api/media-leads/agents').then((r) => r.data),
  /** زبائن المبيعات الذين فات موعدهم بلا إجراء — لإعادة استهدافهم من اللوحة؛ kind يحصرهم بمتابعة أو دفعة مقدمة */
  salesOverdue: (kind?: 'FollowUp' | 'DownPayment') =>
    api
      .get<SalesOverdueRow[]>('/api/media-leads/sales-overdue', { params: { kind } })
      .then((r) => r.data),
  retargetSalesOverdue: (dto: {
    assignmentIds: number[]
    targetComplexId?: number
    assignToEmployeeId?: number
    notes?: string
  }) =>
    api
      .post<{ created: number; skipped: number }>('/api/media-leads/sales-overdue/retarget', dto)
      .then((r) => r.data),
  /** زبائن التطبيق (مدير المبيعات / الرسبشن / طلبات موظفي المبيعات) بمسارهم — افتراضياً آخر ٣٠ يوماً */
  appCustomers: (params: {
    from?: string
    to?: string
    complexId?: number
    creator?: AppCustomerCreator
  }) => api.get<AppCustomersReport>('/api/reports/app-customers', { params }).then((r) => r.data),
  // متابعة مباشرة لأداء موظفي خدمة العملاء (النطاق افتراضياً اليوم)
  agentsMonitor: (params: { from?: string; to?: string }) =>
    api.get<AgentsMonitor>('/api/media-leads/agents-monitor', { params }).then((r) => r.data),
  /** أسماء موظفٍ بالمتابعة المباشرة: المفتوحة الآن + المنجزة بالنطاق ونتيجتها */
  agentTasks: (employeeId: number, params: { from?: string; to?: string }) =>
    api
      .get<MediaLead[]>(`/api/media-leads/agents-monitor/${employeeId}/tasks`, { params })
      .then((r) => r.data),
  // تقرير جودة موظفي الكول سنتر (النطاق افتراضياً آخر ٣٠ يوماً)
  /** أداء موظفي الكول سنتر يومياً: اتصالات كل موظف كل يوم والفرق عن اليوم السابق (افتراضياً آخر ١٤ يوماً) */
  /** تقييم أداء موظفي الكول سنتر: المهام والاتصالات المباشرة والواردة ودرجة كل موظف */
  agentsScorecard: (params: { from?: string; to?: string; employeeId?: number }) =>
    api.get<AgentsScorecard>('/api/media-leads/agents-scorecard', { params }).then((r) => r.data),
  /** تفصيل التقييم: مكالمات الفترة بنتيجةٍ بعينها (ولموظفٍ إن حُدّد) */
  agentCalls: (params: {
    from?: string
    to?: string
    employeeId?: number
    result: MediaLeadStatus
  }) =>
    api
      .get<AgentCallRow[]>('/api/media-leads/agents-scorecard/calls', { params })
      .then((r) => r.data),
  agentsDaily: (params: { from?: string; to?: string }) =>
    api.get<AgentsDailyReport>('/api/media-leads/agents-daily', { params }).then((r) => r.data),
  agentsQuality: (params: { from?: string; to?: string }) =>
    api.get<AgentsQualityReport>('/api/media-leads/agents-quality', { params }).then((r) => r.data),
  // تقرير كل حركات موظفي المبيعات على الزبائن
  salesActions: (params: {
    from?: string
    to?: string
    employeeId?: number
    actionType?: string
    complexId?: number
  }) => api.get<SalesActionsReport>('/api/reports/sales-actions', { params }).then((r) => r.data),
  mediaEmployees: () =>
    api.get<MediaEmployee[]>('/api/media-leads/media-employees').then((r) => r.data),
  setMediaEmployeeComplexes: (employeeId: number, complexIds: number[]) =>
    api
      .put(`/api/media-leads/media-employees/${employeeId}/complexes`, { complexIds })
      .then((r) => r.data),
  // موظف خدمة العملاء
  myTasks: (includeCompleted = false) =>
    api
      .get<MediaLead[]>('/api/media-leads/my-tasks', { params: { includeCompleted } })
      .then((r) => r.data),
  /** عدد مهامي المفتوحة — شارة «الاتصال بالزبائن» بالقائمة */
  myTasksCount: () =>
    api.get<{ open: number }>('/api/media-leads/my-tasks/count').then((r) => r.data),
  /** سجل الزبون قبل الاتصال: ميديا، إعادة استهداف، مكالمات، استقبال، مبيعات */
  history: (id: number) =>
    api.get<LeadHistory>(`/api/media-leads/${id}/history`).then((r) => r.data),
  complete: (
    id: number,
    dto: {
      result: MediaLeadStatus
      notes?: string | null
      bookingDate?: string | null
      /** يُحدَّد لمن جاء بلا مجمع، أو يُحوَّل إن غيّر الزبون رأيه — يظهر لمدير المبيعات */
      residentialComplexId?: number | null
      complexChangeReason?: string | null
      /** اسم الزبون كما عرفه الموظف بالمكالمة — لازمٌ لتأكيد حضور اسمٍ استُورد بلا اسم */
      customerName?: string | null
    },
  ) => api.post<MediaLead>(`/api/media-leads/${id}/complete`, dto).then((r) => r.data),
  // موظف الميديا (متاح أيضاً من الويب)
  mine: () => api.get<MediaLead[]>('/api/media-leads/mine').then((r) => r.data),
  create: (dto: unknown) => api.post<MediaLead>('/api/media-leads', dto).then((r) => r.data),
  // تقرير أرقام الميديا (خدمة العملاء): الأرقام + التكرار — بدون مستحقات
  numbersReport: (params: {
    from?: string
    to?: string
    complexId?: number
    mediaEmployeeId?: number
  }) =>
    api.get<MediaNumbersReport>('/api/media-leads/numbers-report', { params }).then((r) => r.data),
  // مستحقات موظفي الميديا (الموارد البشرية/المحاسبة حصراً)
  payoutsReport: (params: {
    from?: string
    to?: string
    complexId?: number
    mediaEmployeeId?: number
  }) =>
    api.get<MediaNumbersReport>('/api/media-leads/payouts-report', { params }).then((r) => r.data),
}

export const selfServiceApi = {
  dashboard: () => api.get<MyDashboard>('/api/self-service/dashboard').then((r) => r.data),
  profile: () => api.get<Employee>('/api/self-service/profile').then((r) => r.data),
  attendance: (from?: string, to?: string) =>
    api
      .get<AttendanceLog[]>('/api/self-service/attendance', { params: { from, to } })
      .then((r) => r.data),
  checkIn: (dto: { latitude?: number; longitude?: number; notes?: string }) =>
    api.post<AttendanceLog>('/api/self-service/attendance/check-in', dto).then((r) => r.data),
  checkOut: (dto: { latitude?: number; longitude?: number; notes?: string }) =>
    api.post<AttendanceLog>('/api/self-service/attendance/check-out', dto).then((r) => r.data),
  leaves: () => api.get<LeaveRequest[]>('/api/self-service/leaves').then((r) => r.data),
  leaveBalances: () =>
    api.get<LeaveBalance[]>('/api/self-service/leave-balances').then((r) => r.data),
  submitLeave: (dto: { leaveTypeId: number; fromDate: string; toDate: string; reason?: string }) =>
    api
      .post<LeaveRequest>('/api/self-service/leaves', { ...dto, employeeId: 0 })
      .then((r) => r.data),
  payslips: () => api.get<Payslip[]>('/api/self-service/payslips').then((r) => r.data),
  /** يُحدِّث الموظف وسائل تواصله وجهة طوارئه — بلا أجر ولا مصرف ولا هوية */
  updateProfile: (dto: Record<string, unknown>) =>
    api.put<Employee>('/api/self-service/profile', dto).then((r) => r.data),
  loans: () => api.get<EmployeeLoan[]>('/api/self-service/loans').then((r) => r.data),
}

export const employeeAccountApi = {
  createAccount: (employeeId: number, password: string) =>
    api
      .post<{ userId: string }>(`/api/employees/${employeeId}/create-account`, { password })
      .then((r) => r.data),
}

/**
 * أرشيف السِّيَر الذاتية — ملفات المتقدّمين تُقرأ ببثّ مصادَق عليه (blob)
 * لا برابط عام: السيرة بيانات شخصية لا تُترك لمن يخمّن الرابط.
 */
export const recruitmentApi = {
  search: (params: Record<string, unknown>) =>
    api.get<PagedResult<JobApplicantRow>>('/api/recruitment', { params }).then((r) => r.data),
  stats: () => api.get<RecruitmentStats>('/api/recruitment/stats').then((r) => r.data),
  get: (id: number) => api.get<JobApplicant>(`/api/recruitment/${id}`).then((r) => r.data),
  create: (dto: JobApplicantInput) =>
    api.post<JobApplicant>('/api/recruitment', dto).then((r) => r.data),
  update: (id: number, dto: JobApplicantInput) =>
    api.put<JobApplicant>(`/api/recruitment/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/recruitment/${id}`),
  changeStatus: (
    id: number,
    dto: {
      status: JobApplicantStatus
      note?: string | null
      rejectionReason?: string | null
      interviewAt?: string | null
      followUpDate?: string | null
    },
  ) => api.post<JobApplicant>(`/api/recruitment/${id}/status`, dto).then((r) => r.data),
  addNote: (id: number, dto: { text: string; followUpDate?: string | null }) =>
    api.post<JobApplicantNote>(`/api/recruitment/${id}/notes`, dto).then((r) => r.data),
  hire: (id: number) =>
    api
      .post<{ employeeId: number; message: string }>(`/api/recruitment/${id}/hire`)
      .then((r) => r.data),
  uploadFile: (id: number, file: File, kind: JobApplicantFileKind = 'Cv') => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('kind', kind)
    return api
      .post<JobApplicantFile>(`/api/recruitment/${id}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  deleteFile: (fileId: number) => api.delete(`/api/recruitment/files/${fileId}`),
  /** يجلب الملف كـ blob بترويسة المصادقة — يُعرض أو يُنزَّل من الذاكرة */
  fileBlob: (id: number, fileId: number) =>
    api
      .get<Blob>(`/api/recruitment/${id}/files/${fileId}`, { responseType: 'blob' })
      .then((r) => r.data),
}

/** استمارة التقديم العامة — بلا مصادقة */
export const publicJobsApi = {
  titles: () => api.get<PublicJobTitle[]>('/api/public/jobs/titles').then((r) => r.data),
  apply: (form: FormData) =>
    api
      .post<{ message: string; code: string }>('/api/public/jobs/apply', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data),
}

/** التعاميم: إرسال إشعار للموظفين (تطبيق UFUQ MANG ولوحة الويب) وسجل من قرأه */
export const announcementsApi = {
  all: (take = 50) =>
    api.get<Announcement[]>('/api/announcements', { params: { take } }).then((r) => r.data),
  get: (id: number) => api.get<Announcement>(`/api/announcements/${id}`).then((r) => r.data),
  recipients: (id: number) =>
    api.get<AnnouncementRecipient[]>(`/api/announcements/${id}/recipients`).then((r) => r.data),
  audienceOptions: () =>
    api.get<AnnouncementAudienceOptions>('/api/announcements/audience-options').then((r) => r.data),
  preview: (dto: AnnouncementInput) =>
    api.post<AudiencePreview>('/api/announcements/preview', dto).then((r) => r.data),
  send: (dto: AnnouncementInput) =>
    api.post<Announcement>('/api/announcements', dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/announcements/${id}`),
}

/** إشعارات تطبيق UFUQ الإعلانية — التسويق يخاطب زبائن التطبيق بوجهة قابلة للقياس */
export const appCampaignsApi = {
  all: (take = 50) =>
    api.get<AppCampaign[]>('/api/app-campaigns', { params: { take } }).then((r) => r.data),
  options: () => api.get<AppCampaignOptions>('/api/app-campaigns/options').then((r) => r.data),
  preview: (dto: AppCampaignInput) =>
    api.post<AudiencePreview>('/api/app-campaigns/preview', dto).then((r) => r.data),
  send: (dto: AppCampaignInput) =>
    api.post<AppCampaign>('/api/app-campaigns', dto).then((r) => r.data),
  uploadImage: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ path: string }>('/api/app-campaigns/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  remove: (id: number) => api.delete(`/api/app-campaigns/${id}`),
}

export const taxRulesApi = {
  all: () => api.get<TaxRule[]>('/api/tax-rules').then((r) => r.data),
  get: (id: number) => api.get<TaxRule>(`/api/tax-rules/${id}`).then((r) => r.data),
  create: (dto: TaxRuleInput) => api.post<TaxRule>('/api/tax-rules', dto).then((r) => r.data),
  update: (id: number, dto: TaxRuleInput) =>
    api.put<TaxRule>(`/api/tax-rules/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/tax-rules/${id}`),
  preview: (id: number, monthlyGross: number, married: boolean, children: number) =>
    api
      .get<TaxPreview>(`/api/tax-rules/${id}/preview`, {
        params: { monthlyGross, married, children },
      })
      .then((r) => r.data),
}

export const employeesApi = {
  /** تعيين مدير مباشر لقسم أو لمجموعة — أساس شجرة التبعية وسلسلة الاعتماد */
  bulkManager: (dto: {
    departmentId?: number
    employeeIds?: number[]
    directManagerId?: number | null
  }) => api.post<HrBulkResult>('/api/employees/bulk-manager', dto).then((r) => r.data),
  /** ضبط موقع الدوام المسموح لقسم أو لمجموعة موظفين */
  bulkWorkLocation: (dto: {
    departmentId?: number
    employeeIds?: number[]
    latitude: number
    longitude: number
    radiusMeters: number
    enforce: boolean
  }) => api.post<HrBulkResult>('/api/employees/bulk-work-location', dto).then((r) => r.data),
  search: (params: Record<string, unknown>) =>
    api.get<PagedResult<EmployeeListRow>>('/api/employees', { params }).then((r) => r.data),
  /** أسماء الموظفين لقوائم الاختيار — متاحة لكل الأدوار، بلا رواتب ولا بيانات شخصية */
  lookup: (params?: { q?: string; departmentId?: number }) =>
    api.get<EmployeeLookup[]>('/api/employees/lookup', { params }).then((r) => r.data),
  get: (id: number) => api.get<Employee>(`/api/employees/${id}`).then((r) => r.data),
  create: (dto: unknown) => api.post<Employee>('/api/employees', dto).then((r) => r.data),
  update: (id: number, dto: unknown) =>
    api.put<Employee>(`/api/employees/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/employees/${id}`),
  uploadPhoto: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ photoPath: string }>(`/api/employees/${id}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  uploadDocument: (
    id: number,
    data: {
      file: File
      documentType: string
      title: string
      issueDate?: string
      expiryDate?: string
    },
  ) => {
    const fd = new FormData()
    fd.append('file', data.file)
    fd.append('documentType', data.documentType)
    fd.append('title', data.title)
    if (data.issueDate) fd.append('issueDate', data.issueDate)
    if (data.expiryDate) fd.append('expiryDate', data.expiryDate)
    return api
      .post<EmployeeDocument>(`/api/employees/${id}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  deleteDocument: (id: number, docId: number) =>
    api.delete(`/api/employees/${id}/documents/${docId}`),
}

export const stockApi = {
  createMovement: (dto: Partial<StockMovement> & { quantity: number }) =>
    api.post<StockMovement>('/api/stock/movements', dto).then((r) => r.data),
  movements: (params: Record<string, unknown>) =>
    api.get<StockMovement[]>('/api/stock/movements', { params }).then((r) => r.data),
  balances: (itemId?: number, warehouseId?: number) =>
    api
      .get<StockBalance[]>('/api/stock/balances', { params: { itemId, warehouseId } })
      .then((r) => r.data),
  itemLedger: (
    itemId: number,
    from: string,
    to: string,
    variantId?: number,
    warehouseId?: number,
  ) =>
    api
      .get<StockLedgerReport>(`/api/stock/items/${itemId}/ledger`, {
        params: { from, to, variantId, warehouseId },
      })
      .then((r) => r.data),
}

// ═══════════════ Residential Complexes (Real Estate) ═══════════════
import type {
  ResidentialComplexListItem,
  ResidentialComplexDetail,
  ResidentialComplexInput,
  TowerData,
  TowerInput,
  FloorData,
  FloorInput,
  ApartmentData,
  ApartmentInput,
  VillaData,
  VillaInput,
  ApartmentFeature,
  GenerateFloorsInput,
  GenerateApartmentsInput,
  ComplexType,
  AssignedEmployeeDto,
} from '../types'

/** تقرير متابعة الأفرع: قمع خدمة العملاء والاستقبال والمبيعات لكل فرع بجداول موظفيه، ورؤى محسوبة */
export const branchReportApi = {
  get: (params: { from?: string; to?: string; complexId?: number }) =>
    api.get<BranchesReport>('/api/reports/branches', { params }).then((r) => r.data),
}

/** الإدارة المالية للمشاريع — عمولات الموظفين وعمولة التسويق وطرق التسديد */
export const complexFinanceApi = {
  all: () =>
    api.get<ComplexFinanceSummary[]>('/api/residential-complexes/finance').then((r) => r.data),
  get: (id: number) =>
    api.get<ComplexFinance>(`/api/residential-complexes/${id}/finance`).then((r) => r.data),
  save: (id: number, dto: ComplexFinanceInput) =>
    api.put<ComplexFinance>(`/api/residential-complexes/${id}/finance`, dto).then((r) => r.data),
}

export const residentialComplexesApi = {
  all: (type?: ComplexType) =>
    api
      .get<ResidentialComplexListItem[]>('/api/residential-complexes', { params: { type } })
      .then((r) => r.data),
  simple: () =>
    api
      .get<CrmLookup[]>('/api/residential-complexes', { params: { simple: true } })
      .then((r) => r.data),
  get: (id: number) =>
    api.get<ResidentialComplexDetail>(`/api/residential-complexes/${id}`).then((r) => r.data),
  create: (dto: ResidentialComplexInput) =>
    api.post<ResidentialComplexDetail>('/api/residential-complexes', dto).then((r) => r.data),
  update: (id: number, dto: ResidentialComplexInput) =>
    api.put<ResidentialComplexDetail>(`/api/residential-complexes/${id}`, dto).then((r) => r.data),
  /** يحذف المجمع وما بداخله — الخادم يرفض إن كانت له مبيعات */
  remove: (id: number) => api.delete(`/api/residential-complexes/${id}`),
  deleteCheck: (id: number) =>
    api
      .get<ComplexDeleteCheck>(`/api/residential-complexes/${id}/delete-check`)
      .then((r) => r.data),
  stopSales: (id: number, reason: string) =>
    api
      .post<ResidentialComplexDetail>(`/api/residential-complexes/${id}/stop-sales`, { reason })
      .then((r) => r.data),
  resumeSales: (id: number) =>
    api
      .post<ResidentialComplexDetail>(`/api/residential-complexes/${id}/resume-sales`)
      .then((r) => r.data),
  purgeAll: () =>
    api
      .delete<{
        deleted: number
        skipped: string[]
        message: string
      }>('/api/residential-complexes/purge-all?confirm=DELETE_ALL_COMPLEXES')
      .then((r) => r.data),
  uploadMainImage: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ mainImagePath: string }>(`/api/residential-complexes/${id}/main-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  // Employee Assignment (M2M)
  assignedEmployees: (complexId: number) =>
    api
      .get<AssignedEmployeeDto[]>(`/api/residential-complexes/${complexId}/assigned-employees`)
      .then((r) => r.data),
  assignEmployees: (complexId: number, employeeIds: number[]) =>
    api
      .post(`/api/residential-complexes/${complexId}/assigned-employees`, { employeeIds })
      .then((r) => r.data),
  removeAssignment: (complexId: number, employeeId: number) =>
    api.delete(`/api/residential-complexes/${complexId}/assigned-employees/${employeeId}`),
}

import type { UnitStatusBoard, UnitStatusHistoryRow, SetUnitStatusInput } from '../types'

// ═══ Unit Status Workflow (Admin) ═══
export const unitStatusApi = {
  getBoard: (complexId: number) =>
    api.get<UnitStatusBoard>(`/api/complexes/${complexId}/units-status`).then((r) => r.data),
  setApartmentStatus: (apartmentId: number, dto: SetUnitStatusInput) =>
    api.put(`/api/apartments/${apartmentId}/status`, dto).then((r) => r.data),
  setVillaStatus: (villaId: number, dto: SetUnitStatusInput) =>
    api.put(`/api/villas/${villaId}/status`, dto).then((r) => r.data),
  apartmentHistory: (apartmentId: number) =>
    api
      .get<UnitStatusHistoryRow[]>(`/api/apartments/${apartmentId}/status-history`)
      .then((r) => r.data),
  villaHistory: (villaId: number) =>
    api.get<UnitStatusHistoryRow[]>(`/api/villas/${villaId}/status-history`).then((r) => r.data),
}

// ═══ Self-Service: My Complexes ═══
export const myComplexesApi = {
  list: () =>
    api
      .get<ResidentialComplexListItem[]>('/api/self-service/residential-complexes')
      .then((r) => r.data),
  get: (id: number) =>
    api
      .get<ResidentialComplexDetail>(`/api/self-service/residential-complexes/${id}`)
      .then((r) => r.data),
}

export const towersApi = {
  byComplex: (complexId: number) =>
    api.get<TowerData[]>(`/api/residential-complexes/${complexId}/towers`).then((r) => r.data),
  get: (id: number) => api.get<TowerData>(`/api/towers/${id}`).then((r) => r.data),
  create: (complexId: number, dto: TowerInput) =>
    api.post<TowerData>(`/api/residential-complexes/${complexId}/towers`, dto).then((r) => r.data),
  update: (id: number, dto: TowerInput) =>
    api.put<TowerData>(`/api/towers/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/towers/${id}`),
  generateFloors: (id: number, dto: GenerateFloorsInput) =>
    api.post<FloorData[]>(`/api/towers/${id}/generate-floors`, dto).then((r) => r.data),
}

export const floorsApi = {
  byTower: (towerId: number) =>
    api.get<FloorData[]>(`/api/towers/${towerId}/floors`).then((r) => r.data),
  get: (id: number) => api.get<FloorData>(`/api/floors/${id}`).then((r) => r.data),
  create: (towerId: number, dto: FloorInput) =>
    api.post<FloorData>(`/api/towers/${towerId}/floors`, dto).then((r) => r.data),
  update: (id: number, dto: FloorInput) =>
    api.put<FloorData>(`/api/floors/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/floors/${id}`),
  generateApartments: (id: number, dto: GenerateApartmentsInput) =>
    api.post<ApartmentData[]>(`/api/floors/${id}/generate-apartments`, dto).then((r) => r.data),
}

export const apartmentsApi = {
  byFloor: (floorId: number) =>
    api.get<ApartmentData[]>(`/api/floors/${floorId}/apartments`).then((r) => r.data),
  get: (id: number) => api.get<ApartmentData>(`/api/apartments/${id}`).then((r) => r.data),
  create: (floorId: number, dto: ApartmentInput) =>
    api.post<ApartmentData>(`/api/floors/${floorId}/apartments`, dto).then((r) => r.data),
  update: (id: number, dto: ApartmentInput) =>
    api.put<ApartmentData>(`/api/apartments/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/apartments/${id}`),
  uploadImage: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ imagePath: string }>(`/api/apartments/${id}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  uploadPdf: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ pdfPath: string }>(`/api/apartments/${id}/pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
}

export const villasApi = {
  byComplex: (complexId: number) =>
    api.get<VillaData[]>(`/api/residential-complexes/${complexId}/villas`).then((r) => r.data),
  get: (id: number) => api.get<VillaData>(`/api/villas/${id}`).then((r) => r.data),
  create: (complexId: number, dto: VillaInput) =>
    api.post<VillaData>(`/api/residential-complexes/${complexId}/villas`, dto).then((r) => r.data),
  update: (id: number, dto: VillaInput) =>
    api.put<VillaData>(`/api/villas/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/villas/${id}`),
  uploadImage: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ imagePath: string }>(`/api/villas/${id}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  uploadPdf: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ pdfPath: string }>(`/api/villas/${id}/pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
}

export const apartmentFeaturesApi = {
  all: () => api.get<ApartmentFeature[]>('/api/apartment-features').then((r) => r.data),
  create: (dto: Partial<ApartmentFeature>) =>
    api.post<ApartmentFeature>('/api/apartment-features', dto).then((r) => r.data),
  update: (id: number, dto: Partial<ApartmentFeature>) =>
    api.put<ApartmentFeature>(`/api/apartment-features/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/apartment-features/${id}`),
}

// ═══ Sales contracts (عقود البيع والأقساط) ═══
export const saleContractsApi = {
  list: () => api.get<SaleContractRow[]>('/api/sale-contracts').then((r) => r.data),
  get: (id: number) => api.get<SaleContract>(`/api/sale-contracts/${id}`).then((r) => r.data),
  create: (dto: SaleContractCreate) =>
    api.post<SaleContract>('/api/sale-contracts', dto).then((r) => r.data),
  recordPayment: (
    id: number,
    dto: {
      amount: number
      paymentDate?: string
      method?: string
      saleInstallmentId?: number
      notes?: string
    },
  ) => api.post(`/api/sale-contracts/${id}/payments`, dto).then((r) => r.data),
  cancel: (id: number) => api.post(`/api/sale-contracts/${id}/cancel`, {}).then((r) => r.data),
  approve: (id: number) => api.post(`/api/sale-contracts/${id}/approve`, {}).then((r) => r.data),
  reject: (id: number) => api.post(`/api/sale-contracts/${id}/reject`, {}).then((r) => r.data),
  acquire: (dto: {
    apartmentId?: number
    villaId?: number
    cost: number
    budgetId?: number
    fundingAccountId?: number
  }) => api.post(`/api/sale-contracts/acquire`, dto).then((r) => r.data),
  collectCommission: (id: number, dto: { amount: number; fundingAccountId?: number }) =>
    api.post(`/api/sale-contracts/${id}/collect-commission`, dto).then((r) => r.data),
  acquireBatch: (dto: BatchAcquisition) =>
    api.post(`/api/sale-contracts/acquire-batch`, dto).then((r) => r.data),
  // ميزانية شراء العقارات
  budgets: (complexId?: number) =>
    api
      .get<RealEstateBudget[]>('/api/sale-contracts/budgets', { params: { complexId } })
      .then((r) => r.data),
  createBudget: (dto: RealEstateBudgetInput) =>
    api.post<RealEstateBudget>('/api/sale-contracts/budgets', dto).then((r) => r.data),
  closeBudget: (id: number) =>
    api.post(`/api/sale-contracts/budgets/${id}/close`, {}).then((r) => r.data),
  realEstateProfit: () =>
    api.get<RealEstateProfitReport>('/api/sale-contracts/real-estate-profit').then((r) => r.data),
  /** تقرير المستحقات الموحّد ضمن أيام (-1 = الكل) */
  duePayments: (days = 30) =>
    api
      .get<DuePaymentsReport>('/api/sale-contracts/due-payments', { params: { days } })
      .then((r) => r.data),
  /** ملخّص تنبيه فتح الويب لمن يتابع المستحقات */
  dueAlert: () => api.get<DueAlert>('/api/sale-contracts/due-payments/alert').then((r) => r.data),
  dueInstallments: (overdueOnly = false) =>
    api
      .get<
        SaleDueInstallment[]
      >('/api/sale-contracts/due-installments', { params: { overdueOnly } })
      .then((r) => r.data),
  commissions: () =>
    api.get<SaleCommissionRow[]>('/api/sale-contracts/commissions').then((r) => r.data),
  summary: () => api.get<SalesSummary>('/api/sale-contracts/summary').then((r) => r.data),
  preliminaryReservations: () =>
    api
      .get<PreliminaryReservation[]>('/api/sale-contracts/preliminary-reservations')
      .then((r) => r.data),
  /** تحرير حجز: الوحدة متاحة، وملف الزبون بالتطبيق يعود لما قبل الحجز ويُبلَّغ موظفه */
  releaseReservation: (dto: {
    unitKind: PreliminaryReservation['unitKind']
    unitId: number
    actionId?: number | null
    reason?: string
  }) => api.post('/api/sale-contracts/reservations/release', dto).then((r) => r.data),
  /** مبيعات تطبيق المبيعات بانتظار عقد البيع */
  pendingSales: () =>
    api.get<PendingSale[]>('/api/sale-contracts/pending-sales').then((r) => r.data),
  customerFollowUps: (customerId: number) =>
    api
      .get<CustomerFollowUpEntry[]>(`/api/self-service/customers/${customerId}/follow-ups`)
      .then((r) => r.data),
}

/** طلبات موظفي المبيعات تعديل عمولة التسويق — يبتّ فيها مدير المبيعات */
export const commissionChangesApi = {
  /** طلبات تعديل العمولة والمقدمة */
  list: (pendingOnly = true) =>
    api
      .get<CommissionChange[]>('/api/sales-manager/commission-changes', {
        params: { pendingOnly, includeDownPayment: true },
      })
      .then((r) => r.data),
  decide: (
    actionId: number,
    approve: boolean,
    note?: string,
    kind: CommissionChange['kind'] = 'Commission',
  ) =>
    api
      .post(
        `/api/sales-manager/${kind === 'DownPayment' ? 'down-payment-changes' : 'commission-changes'}/${actionId}/${approve ? 'approve' : 'reject'}`,
        { note: note ?? null },
      )
      .then((r) => r.data),
}

// ═══ Marketing (التسويق) ═══
import type {
  CampaignRow,
  Campaign,
  CampaignCreate,
  CampaignStatus,
  CampaignDisbursementRow,
  CampaignVoucher,
  ContentItem,
  ContentItemCreate,
  ContentStatus,
  ProductionRequest,
  ProductionRequestCreate,
  ProductionStatus,
  MarketingSupplier,
  MarketingBudget,
  MarketingBudgetCreate,
  MarketingCost,
  MarketingCostCreate,
  BudgetVsActualRow,
  MarketingSummary,
  CampaignLookup,
  EffectivenessReport,
} from '../types'

export const marketingApi = {
  // campaigns
  campaigns: () => api.get<CampaignRow[]>('/api/marketing/campaigns').then((r) => r.data),
  campaign: (id: number) => api.get<Campaign>(`/api/marketing/campaigns/${id}`).then((r) => r.data),
  createCampaign: (dto: CampaignCreate) =>
    api.post<Campaign>('/api/marketing/campaigns', dto).then((r) => r.data),
  updateCampaign: (id: number, dto: CampaignCreate) =>
    api.put(`/api/marketing/campaigns/${id}`, dto).then((r) => r.data),
  setCampaignStatus: (id: number, status: CampaignStatus) =>
    api.post(`/api/marketing/campaigns/${id}/status`, { status }).then((r) => r.data),
  addMetric: (
    id: number,
    dto: {
      date?: string
      spend: number
      reach: number
      impressions: number
      clicks: number
      leads: number
      notes?: string
    },
  ) => api.post(`/api/marketing/campaigns/${id}/metrics`, dto).then((r) => r.data),
  submitCampaign: (id: number) =>
    api.post(`/api/marketing/campaigns/${id}/submit`, {}).then((r) => r.data),
  approveCampaign: (id: number, amount?: number) =>
    api.post(`/api/marketing/campaigns/${id}/approve`, { amount }).then((r) => r.data),
  rejectCampaign: (id: number, note: string) =>
    api.post(`/api/marketing/campaigns/${id}/reject`, { note }).then((r) => r.data),
  pendingDisbursements: () =>
    api
      .get<CampaignDisbursementRow[]>('/api/marketing/campaigns/pending-disbursement')
      .then((r) => r.data),
  disburseCampaign: (id: number) =>
    api.post<CampaignVoucher>(`/api/marketing/campaigns/${id}/disburse`, {}).then((r) => r.data),
  returnDisbursement: (id: number, note: string) =>
    api.post(`/api/marketing/campaigns/${id}/return-disbursement`, { note }).then((r) => r.data),
  uploadCampaignImage: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ id: number; path: string }>(`/api/marketing/campaigns/${id}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  deleteCampaignImage: (imageId: number) =>
    api.delete(`/api/marketing/campaigns/images/${imageId}`).then((r) => r.data),
  addCampaignLink: (
    id: number,
    dto: { url: string; linkType: 'Image' | 'Video'; title?: string },
  ) => api.post(`/api/marketing/campaigns/${id}/links`, dto).then((r) => r.data),
  deleteCampaignLink: (linkId: number) =>
    api.delete(`/api/marketing/campaigns/links/${linkId}`).then((r) => r.data),
  // content
  content: (params?: { year?: number; month?: number; complexId?: number }) =>
    api.get<ContentItem[]>('/api/marketing/content', { params }).then((r) => r.data),
  createContent: (dto: ContentItemCreate) =>
    api.post<ContentItem>('/api/marketing/content', dto).then((r) => r.data),
  updateContent: (id: number, dto: ContentItemCreate) =>
    api.put(`/api/marketing/content/${id}`, dto).then((r) => r.data),
  setContentStatus: (id: number, status: ContentStatus) =>
    api.post(`/api/marketing/content/${id}/status`, { status }).then((r) => r.data),
  deleteContent: (id: number) => api.delete(`/api/marketing/content/${id}`).then((r) => r.data),
  // production
  production: () => api.get<ProductionRequest[]>('/api/marketing/production').then((r) => r.data),
  createProduction: (dto: ProductionRequestCreate) =>
    api.post<ProductionRequest>('/api/marketing/production', dto).then((r) => r.data),
  updateProduction: (id: number, dto: ProductionRequestCreate) =>
    api.put(`/api/marketing/production/${id}`, dto).then((r) => r.data),
  setProductionStatus: (id: number, status: ProductionStatus) =>
    api.post(`/api/marketing/production/${id}/status`, { status }).then((r) => r.data),
  // suppliers
  suppliers: () => api.get<MarketingSupplier[]>('/api/marketing/suppliers').then((r) => r.data),
  createSupplier: (dto: Omit<MarketingSupplier, 'id'>) =>
    api.post<MarketingSupplier>('/api/marketing/suppliers', dto).then((r) => r.data),
  updateSupplier: (id: number, dto: Omit<MarketingSupplier, 'id'>) =>
    api.put(`/api/marketing/suppliers/${id}`, dto).then((r) => r.data),
  // budgets & costs
  budgets: (year?: number) =>
    api.get<MarketingBudget[]>('/api/marketing/budgets', { params: { year } }).then((r) => r.data),
  createBudget: (dto: MarketingBudgetCreate) =>
    api.post<MarketingBudget>('/api/marketing/budgets', dto).then((r) => r.data),
  deleteBudget: (id: number) => api.delete(`/api/marketing/budgets/${id}`).then((r) => r.data),
  costs: (params?: { year?: number; month?: number }) =>
    api.get<MarketingCost[]>('/api/marketing/costs', { params }).then((r) => r.data),
  createCost: (dto: MarketingCostCreate) =>
    api.post<MarketingCost>('/api/marketing/costs', dto).then((r) => r.data),
  deleteCost: (id: number) => api.delete(`/api/marketing/costs/${id}`).then((r) => r.data),
  budgetVsActual: (year?: number) =>
    api
      .get<BudgetVsActualRow[]>('/api/marketing/budget-vs-actual', { params: { year } })
      .then((r) => r.data),
  summary: () => api.get<MarketingSummary>('/api/marketing/summary').then((r) => r.data),
  activeCampaigns: (complexId?: number) =>
    api
      .get<CampaignLookup[]>('/api/marketing/active-campaigns', { params: { complexId } })
      .then((r) => r.data),
  effectiveness: (params?: { complexId?: number; from?: string; to?: string }) =>
    api.get<EffectivenessReport>('/api/marketing/effectiveness', { params }).then((r) => r.data),
}

/** التاركت الشهري + العمولة (نسبة إنجاز التاركت × عمولة الشقة بسقف ٢٠٠٪) */
export const salesTargetsApi = {
  complexOptions: () =>
    api.get<ComplexOption[]>('/api/sales-targets/complex-options').then((r) => r.data),
  centers: (year: number, month: number) =>
    api
      .get<CenterTarget[]>('/api/sales-targets/centers', { params: { year, month } })
      .then((r) => r.data),
  /** التاركت: عدد الشقق فقط — العمولات وطرق التسديد من «الإدارة المالية للمشاريع» */
  upsertCenter: (dto: {
    year: number
    month: number
    residentialComplexId: number
    targetUnits: number
    notes?: string
  }) => api.post<CenterTarget>('/api/sales-targets/center', dto).then((r) => r.data),
  /** المدير الإقليمي يعتمد توزيع مدير المبيعات أو يرفضه بسببه */
  decide: (centerTargetId: number, approve: boolean, reason?: string) =>
    api
      .post<CenterDistribution>(`/api/sales-targets/center/${centerTargetId}/decision`, {
        approve,
        reason,
      })
      .then((r) => r.data),
  deleteCenter: (id: number) => api.delete(`/api/sales-targets/center/${id}`),
  distribution: (centerTargetId: number) =>
    api
      .get<CenterDistribution>(`/api/sales-targets/center/${centerTargetId}/distribution`)
      .then((r) => r.data),
  setDistribution: (centerTargetId: number, items: { employeeId: number; targetUnits: number }[]) =>
    api
      .post<CenterDistribution>(`/api/sales-targets/center/${centerTargetId}/distribution`, {
        items,
      })
      .then((r) => r.data),
  report: (year: number, month: number) =>
    api
      .get<TargetsReport>('/api/sales-targets/report', { params: { year, month } })
      .then((r) => r.data),
}

/** الواردة — محادثات واتساب/ماسنجر/انستكرام */
/** ربط قناة خارجية «المحور» — الإعدادات للأدمن، والباقي لمدير خدمة العملاء أيضاً */
export const mihwarApi = {
  status: () => api.get<MihwarStatus>('/api/integrations/mihwar/status').then((r) => r.data),
  /** null = يبقى كما هو، '' = يُمسح */
  saveSettings: (dto: { apiKey?: string | null; webhookSecret?: string | null }) =>
    api.put<MihwarStatus>('/api/integrations/mihwar/settings', dto).then((r) => r.data),
  test: () => api.post<MihwarTest>('/api/integrations/mihwar/test').then((r) => r.data),
  /** يجلب المحادثات ورسائلها السابقة من المحور إلى الواردة — بلا تكرار */
  sync: () =>
    api
      .post<{
        conversations: number
        newMessages: number
        partial: boolean
      }>('/api/integrations/mihwar/sync')
      .then((r) => r.data),
  contacts: (params?: { q?: string; page?: number }) =>
    api.get<MihwarContacts>('/api/integrations/mihwar/contacts', { params }).then((r) => r.data),
  deliveries: (take = 50) =>
    api
      .get<MihwarDelivery[]>('/api/integrations/mihwar/deliveries', { params: { take } })
      .then((r) => r.data),
  replay: (id: number) =>
    api
      .post<MihwarDelivery>(`/api/integrations/mihwar/deliveries/${id}/replay`)
      .then((r) => r.data),
  /** قنوات حساب المحور (صفحات وأرقام) وربطها بقنوات الواردة */
  channels: () => api.get<MihwarChannel[]>('/api/integrations/mihwar/channels').then((r) => r.data),
  /** ربط قناة المحور بمجمع — بلا Page ID ولا توكن */
  linkChannel: (id: string, dto: { residentialComplexId?: number | null; name?: string }) =>
    api
      .post<MihwarChannel>(`/api/integrations/mihwar/channels/${encodeURIComponent(id)}/link`, dto)
      .then((r) => r.data),
}

export const inboxApi = {
  conversations: (params?: {
    platform?: string
    status?: string
    search?: string
    complexId?: number
    /** طريقة ربطٍ بعينها داخل المشروع */
    channelId?: number
  }) => api.get<SocialConversation[]>('/api/inbox/conversations', { params }).then((r) => r.data),
  summary: () => api.get<InboxSummary>('/api/inbox/summary').then((r) => r.data),
  messages: (id: number, markRead = true) =>
    api
      .get<SocialMessage[]>(`/api/inbox/conversations/${id}/messages`, { params: { markRead } })
      .then((r) => r.data),
  replyMedia: (id: number, file: File, caption?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    if (caption) fd.append('caption', caption)
    return api
      .post<SocialMessage>(`/api/inbox/conversations/${id}/reply-media`, fd)
      .then((r) => r.data)
  },
  reply: (id: number, text: string) =>
    api.post<SocialMessage>(`/api/inbox/conversations/${id}/reply`, { text }).then((r) => r.data),
  assign: (id: number, employeeId: number | null) =>
    api.post(`/api/inbox/conversations/${id}/assign`, { employeeId }),
  setStatus: (id: number, status: SocialConversationStatus) =>
    api.post(`/api/inbox/conversations/${id}/status`, null, { params: { status } }),
  convert: (
    id: number,
    dto: {
      customerName: string
      customerPhone: string
      country?: string
      countryDialCode?: string
      residentialComplexId?: number
      notes?: string
    },
  ) =>
    api
      .post<{ mediaLeadId: number }>(`/api/inbox/conversations/${id}/convert`, dto)
      .then((r) => r.data),
  /** ربط المحادثة بالزبون الذي أُضيف منها بـ«إضافة معلومات الزبائن» */
  linkCustomer: (id: number, customerId: number) =>
    api.post(`/api/inbox/conversations/${id}/link-customer`, { customerId }).then((r) => r.data),
  // ── إدارة القنوات ──
  channels: () => api.get<InboxChannel[]>('/api/inbox/channels').then((r) => r.data),
  createChannel: (dto: InboxChannelInput) =>
    api.post<InboxChannel>('/api/inbox/channels', dto).then((r) => r.data),
  updateChannel: (id: number, dto: InboxChannelInput) =>
    api.put<InboxChannel>(`/api/inbox/channels/${id}`, dto).then((r) => r.data),
  deleteChannel: (id: number) => api.delete(`/api/inbox/channels/${id}`),
  testChannel: (id: number) =>
    api.post<{ message: string }>(`/api/inbox/channels/${id}/test`).then((r) => r.data),
  registerWebhook: (id: number) =>
    api.post<{ message: string }>(`/api/inbox/channels/${id}/register-webhook`).then((r) => r.data),
  // ── ربط واتساب بمسح QR ──
  startQrSession: (id: number) =>
    api.post<QrSession>(`/api/inbox/channels/${id}/qr-session`).then((r) => r.data),
  qrSessionStatus: (id: number) =>
    api.get<QrSession>(`/api/inbox/channels/${id}/qr-session`).then((r) => r.data),
  qrLogout: (id: number) =>
    api.post<{ message: string }>(`/api/inbox/channels/${id}/qr-logout`).then((r) => r.data),
}

// ───── كتالوج التسويق العقاري — قسم «التسويق العقاري» بالويب ─────

export const marketingCatalogApi = {
  all: () => api.get<MarketedProject[]>('/api/marketing-catalog').then((r) => r.data),
  byId: (id: number) =>
    api.get<MarketedProject>(`/api/marketing-catalog/${id}`).then((r) => r.data),
  create: (dto: MarketedProjectInput) =>
    api.post<MarketedProject>('/api/marketing-catalog', dto).then((r) => r.data),
  update: (id: number, dto: MarketedProjectInput) =>
    api.put<MarketedProject>(`/api/marketing-catalog/${id}`, dto).then((r) => r.data),
  remove: (id: number) => api.delete(`/api/marketing-catalog/${id}`),
  reorder: (ids: number[]) => api.post('/api/marketing-catalog/reorder', { ids }),
  uploadImage: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ path: string }>(`/api/marketing-catalog/${id}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  deleteImage: (imageId: number) => api.delete(`/api/marketing-catalog/images/${imageId}`),
  setCover: (id: number, imageId: number) =>
    api.post(`/api/marketing-catalog/${id}/cover/${imageId}`),
  uploadMasterPlan: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ path: string }>(`/api/marketing-catalog/${id}/master-plan`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  banners: () => api.get<MarketingBanner[]>('/api/marketing-catalog/banners').then((r) => r.data),
  createBanner: (dto: MarketingBannerInput) =>
    api.post<MarketingBanner>('/api/marketing-catalog/banners', dto).then((r) => r.data),
  updateBanner: (id: number, dto: MarketingBannerInput) =>
    api.put<MarketingBanner>(`/api/marketing-catalog/banners/${id}`, dto).then((r) => r.data),
  deleteBanner: (id: number) => api.delete(`/api/marketing-catalog/banners/${id}`),
  reorderBanners: (ids: number[]) => api.post('/api/marketing-catalog/banners/reorder', { ids }),
  deleteBannerImage: (imageId: number) =>
    api.delete(`/api/marketing-catalog/banners/images/${imageId}`),
  reorderBannerImages: (bannerId: number, ids: number[]) =>
    api.post(`/api/marketing-catalog/banners/${bannerId}/images/reorder`, { ids }),
  mostSearched: (days = 30) =>
    api
      .get<MostSearchedRow[]>('/api/marketing-catalog/most-searched', { params: { days } })
      .then((r) => r.data),
  developers: () => api.get<Developer[]>('/api/marketing-catalog/developers').then((r) => r.data),
  createDeveloper: (dto: DeveloperInput) =>
    api.post<Developer>('/api/marketing-catalog/developers', dto).then((r) => r.data),
  updateDeveloper: (id: number, dto: DeveloperInput) =>
    api.put<Developer>(`/api/marketing-catalog/developers/${id}`, dto).then((r) => r.data),
  deleteDeveloper: (id: number) => api.delete(`/api/marketing-catalog/developers/${id}`),
  uploadDeveloperLogo: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ path: string }>(`/api/marketing-catalog/developers/${id}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  homeSection: (section: HomeSectionKey) =>
    api
      .get<HomeSectionItem[]>(`/api/marketing-catalog/home-sections/${section}`)
      .then((r) => r.data),
  setHomeSection: (section: HomeSectionKey, projectIds: number[]) =>
    api
      .put<HomeSectionItem[]>(`/api/marketing-catalog/home-sections/${section}`, { projectIds })
      .then((r) => r.data),
  uploadBannerImage: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ path: string }>(`/api/marketing-catalog/banners/${id}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  funnel: (from: string, to: string) =>
    api
      .get<ProjectFunnel[]>('/api/marketing-catalog/funnel', { params: { from, to } })
      .then((r) => r.data),
  settings: () => api.get<MarketingSettings>('/api/marketing-catalog/settings').then((r) => r.data),
  updateSettings: (dto: MarketingSettings) =>
    api.put<MarketingSettings>('/api/marketing-catalog/settings', dto).then((r) => r.data),
  testimonials: () =>
    api.get<Testimonial[]>('/api/marketing-catalog/testimonials').then((r) => r.data),
  createTestimonial: (dto: TestimonialInput) =>
    api.post<Testimonial>('/api/marketing-catalog/testimonials', dto).then((r) => r.data),
  updateTestimonial: (id: number, dto: TestimonialInput) =>
    api.put<Testimonial>(`/api/marketing-catalog/testimonials/${id}`, dto).then((r) => r.data),
  deleteTestimonial: (id: number) => api.delete(`/api/marketing-catalog/testimonials/${id}`),
  appAccounts: (search?: string) =>
    api
      .get<AppAccount[]>('/api/marketing-catalog/app-accounts', { params: { search } })
      .then((r) => r.data),
  uploadUnitPlan: (unitModelId: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<{ path: string }>(`/api/marketing-catalog/unit-models/${unitModelId}/plan`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
}

/** دردشة تطبيق أفق — جهة الموظفين: المختص يرى مشاريعه والكول سنتر يرى الكل */
export const appChatApi = {
  list: (open?: boolean, search?: string) =>
    api
      .get<StaffChatConversation[]>('/api/app-chat', { params: { open, search } })
      .then((r) => r.data),
  summary: () => api.get<AppChatSummary>('/api/app-chat/summary').then((r) => r.data),
  messages: (id: number, afterId = 0) =>
    api
      .get<StaffChatMessagesPage>(`/api/app-chat/${id}/messages`, { params: { afterId } })
      .then((r) => r.data),
  reply: (id: number, body: string) =>
    api.post<StaffChatMessage>(`/api/app-chat/${id}/reply`, { body }).then((r) => r.data),
  close: (id: number) => api.post(`/api/app-chat/${id}/close`),
  reopen: (id: number) => api.post(`/api/app-chat/${id}/reopen`),
  assign: (id: number, userId: string | null) => api.post(`/api/app-chat/${id}/assign`, { userId }),
  assignees: () => api.get<ChatAssignee[]>('/api/app-chat/assignees').then((r) => r.data),
  aiReply: (id: number) =>
    api.post<StaffChatMessage>(`/api/app-chat/${id}/ai-reply`).then((r) => r.data),
  settings: () => api.get<AppChatSettings>('/api/app-chat/settings').then((r) => r.data),
  updateSettings: (dto: AppChatSettings) =>
    api.put<AppChatSettings>('/api/app-chat/settings', dto).then((r) => r.data),
}

/** الواجهة العامة بلا مصادقة — صفحة الهبوط /p/:id تستهلك نفس DTO تطبيق UFUQ */
export const publicCatalogApi = {
  project: (id: number) =>
    api.get<PublicProjectDetail>(`/api/public/projects/${id}`).then((r) => r.data),
  track: (marketedProjectId: number, event: 'View' | 'WhatsApp' | 'Call') =>
    api.post('/api/public/track', { marketedProjectId, event }).catch(() => undefined),
}

// ─── «الشريك العقاري» — مراجعة طلبات الشركاء واعتمادها (التسويق العقاري) ───
import type {
  RealEstatePartnerDetail,
  RealEstatePartnerList,
  ListingReportList,
  PartnerListingDetail,
  PartnerListingList,
  PartnerListingStatus,
  ListingOfferType,
  ListingPropertyType,
  RealEstatePartnerStatus,
  RealEstatePartnerType,
} from '../types'

export const realEstatePartnersApi = {
  list: (params: { status?: RealEstatePartnerStatus; type?: RealEstatePartnerType; q?: string }) =>
    api.get<RealEstatePartnerList>('/api/real-estate-partners', { params }).then((r) => r.data),
  get: (id: number) =>
    api.get<RealEstatePartnerDetail>(`/api/real-estate-partners/${id}`).then((r) => r.data),
  /** وثيقة التحقق كـ blob بترويسة المصادقة — تُعرض من ذاكرة المتصفح لا برابط عام */
  documentBlob: (id: number, documentId: number) =>
    api
      .get<Blob>(`/api/real-estate-partners/${id}/documents/${documentId}`, {
        responseType: 'blob',
      })
      .then((r) => r.data),
  approve: (id: number) =>
    api
      .post<RealEstatePartnerDetail>(`/api/real-estate-partners/${id}/approve`)
      .then((r) => r.data),
  reject: (id: number, reason: string) =>
    api
      .post<RealEstatePartnerDetail>(`/api/real-estate-partners/${id}/reject`, { reason })
      .then((r) => r.data),
  suspend: (id: number, reason: string) =>
    api
      .post<RealEstatePartnerDetail>(`/api/real-estate-partners/${id}/suspend`, { reason })
      .then((r) => r.data),
  reactivate: (id: number) =>
    api
      .post<RealEstatePartnerDetail>(`/api/real-estate-partners/${id}/reactivate`)
      .then((r) => r.data),
}

/** «قبول عقارات الشركاء» — قبول ونشر، رفض بسبب، إيقاف النشر وإعادته */
export const partnerListingsApi = {
  list: (params: {
    status?: PartnerListingStatus
    offer?: ListingOfferType
    type?: ListingPropertyType
    q?: string
  }) => api.get<PartnerListingList>('/api/partner-listings', { params }).then((r) => r.data),
  get: (id: number) =>
    api.get<PartnerListingDetail>(`/api/partner-listings/${id}`).then((r) => r.data),
  approve: (id: number) =>
    api.post<PartnerListingDetail>(`/api/partner-listings/${id}/approve`).then((r) => r.data),
  reject: (id: number, reason: string) =>
    api
      .post<PartnerListingDetail>(`/api/partner-listings/${id}/reject`, { reason })
      .then((r) => r.data),
  unpublish: (id: number, reason: string) =>
    api
      .post<PartnerListingDetail>(`/api/partner-listings/${id}/unpublish`, { reason })
      .then((r) => r.data),
  republish: (id: number) =>
    api.post<PartnerListingDetail>(`/api/partner-listings/${id}/republish`).then((r) => r.data),
  /** بلاغات المستخدمين — المفتوحة أو المحسومة */
  reports: (resolved = false) =>
    api
      .get<ListingReportList>('/api/partner-listings/reports', { params: { resolved } })
      .then((r) => r.data),
  /** unpublish = إيقاف نشر الإعلان (يحسم كل بلاغاته)، وإلا تجاهل هذا البلاغ */
  resolveReport: (reportId: number, dto: { unpublish: boolean; note?: string }) =>
    api.post(`/api/partner-listings/reports/${reportId}/resolve`, dto).then((r) => r.data),
}
